/**
 * Tool Thinking Summary Generator
 *
 * Maps tool names to human-readable "thinking" summaries based on tool input args.
 * Used in non-debug mode to show compact status lines instead of verbose tool displays.
 *
 * Pure function — no server-side imports, uses only the tool's input object.
 */

type ToolInput = Record<string, unknown>;

/**
 * Custom summary generators for tools with meaningful input args.
 * Each returns a present-participle phrase like "Searching for React developers".
 */
const customGenerators: Record<string, (input: ToolInput) => string> = {
  // Jobs and submissions
  createJob: () => "Creating job from the brief",
  createSubmission: () => "Creating submission",
  createSubmissionEmail: (input) => {
    const subject = input.subject as string | undefined;
    return subject ? `Drafting submission email: ${truncate(subject, 50)}` : "Drafting submission email";
  },
  sendSubmissionEmail: () => "Preparing to send submission email",
  importCandidateFromText: () => "Importing candidate from CV text",
  matchCandidatesToJob: () => "Ranking candidates against the job",
  findHiringManagers: () => "Researching hiring managers",

  // Lookup
  findRecords: (input) => {
    const names = input.names as string[] | undefined;
    const type = (input.type as string | undefined) ?? "record";
    return names?.length ? `Looking up ${type}${names.length > 1 ? "s" : ""}: ${truncate(names.join(", "), 50)}` : `Looking up ${type}s`;
  },

  // Analytics
  queryData: (input) => {
    const explanation = input.explanation as string | undefined;
    return explanation ? `Querying data: ${truncate(explanation, 60)}` : "Querying data";
  },
  exportAnalyticsData: () => "Exporting analytics data",
  planReport: (input) => {
    const topic = input.topic as string | undefined;
    return topic ? `Planning report: ${truncate(topic, 50)}` : "Planning report";
  },

  // Candidates
  searchCandidates: (input) => {
    const parts = [
      ...((input.skills as string[] | undefined) ?? []),
      ...((input.regions as string[] | undefined) ?? []),
      ...((input.locations as string[] | undefined) ?? []),
      ...((input.experienceKeywords as string[] | undefined) ?? []).slice(0, 1),
      ...((input.titles as string[] | undefined) ?? []).slice(0, 1),
    ];
    return parts.length ? `Searching candidates: ${truncate(parts.join(", "), 60)}` : "Searching candidates";
  },
  getCandidate: () => "Fetching candidate details",
  createCandidate: (input) => {
    const name = input.name as string | undefined;
    if (input.solaUploadId) return name ? `Importing ${name} from CV` : "Importing candidate from CV";
    return name ? `Creating candidate: ${name}` : "Creating candidate";
  },
  deleteCandidate: () => "Deleting candidate",
  updateCandidateData: () => "Updating candidate data",
  addCandidateNote: () => "Adding candidate note",
  importCandidateFromResume: () => "Importing candidate from resume",
  generateResume: () => "Generating resume",
  addCandidateSkill: (input) => {
    const skill = input.skill as string | undefined;
    return skill ? `Adding skill: ${skill}` : "Adding candidate skill";
  },
  removeCandidateSkill: (input) => {
    const skill = input.skill as string | undefined;
    return skill ? `Removing skill: ${skill}` : "Removing candidate skill";
  },
  addCandidateRole: () => "Adding work experience",
  updateCandidateRole: () => "Updating work experience",
  removeCandidateRole: () => "Removing work experience",
  addCandidateProject: () => "Adding project",
  updateCandidateProject: () => "Updating project",
  removeCandidateProject: () => "Removing project",
  addCandidateEducation: () => "Adding education",
  updateCandidateEducation: () => "Updating education",
  removeCandidateEducation: () => "Removing education",

  // Contacts
  searchContacts: (input) => {
    const search = input.search as string | undefined;
    return search ? `Searching contacts for "${truncate(search, 40)}"` : "Searching contacts";
  },
  getContact: () => "Fetching contact details",
  createContact: (input) => {
    const name = (input.firstName as string) || (input.name as string) || undefined;
    return name ? `Creating contact: ${name}` : "Creating contact";
  },
  updateContact: () => "Updating contact",
  deleteContact: () => "Deleting contact",

  // Floats
  searchFloats: (input) => {
    const search = input.search as string | undefined;
    return search ? `Searching floats for "${truncate(search, 40)}"` : "Searching floats";
  },
  getFloat: () => "Fetching float details",
  createFloat: (input) => {
    const name = input.name as string | undefined;
    return name ? `Creating float: ${truncate(name, 40)}` : "Creating float";
  },
  updateFloat: () => "Updating float",
  deleteFloat: () => "Deleting float",
  fetchCandidatesForFloat: () => "Fetching candidates for float",
  duplicateFloat: () => "Duplicating float",
  getFloatInteractions: () => "Fetching float interactions",
  createShareLink: () => "Creating share link",

  // Research
  searchWeb: (input) => {
    const query = input.query as string | undefined;
    return query ? `Searching the web for "${truncate(query, 50)}"` : "Searching the web";
  },

  // Email
  getEmailDraft: () => "Fetching email draft",
  createFloatEmail: () => "Creating email",
  sendTestEmail: () => "Sending test email",
  sendEmailCampaign: () => "Preparing to send float email",
  createScheduledMessage: () => "Creating scheduled message",
  getScheduledMessages: () => "Fetching scheduled messages",

  // Dashboard
  getDashboardStats: () => "Fetching dashboard stats",
  getTeamDashboardStats: () => "Fetching team dashboard stats",
  getTopLeads: () => "Fetching top leads",

  // Integrations
  getIntegrationStatus: () => "Checking integration status",
  disconnectIntegration: () => "Disconnecting integration",
  proxyIntegrationRequest: () => "Making integration request",
  clearIntegrationCache: () => "Clearing integration cache",

  // Team
  getTeamCandidates: () => "Fetching team candidates",
  getTeamCandidate: () => "Fetching team candidate",
  duplicateTeamCandidate: () => "Duplicating team candidate",
  getTeamFloats: () => "Fetching team floats",
  getTeamFloat: () => "Fetching team float",
  duplicateTeamFloat: () => "Duplicating team float",
  getTeamContacts: () => "Fetching team contacts",
  getTeamContact: () => "Fetching team contact",
  duplicateTeamContact: () => "Duplicating team contact",

  // Notifications
  getNotifications: () => "Fetching notifications",
  markNotificationsRead: () => "Marking notifications as read",

  // Tags
  getTags: () => "Fetching tags",

  // Quality Control
  judgeConversation: () => "Evaluating conversation quality",

  // Reports
  writeAnalysisReport: () => "Writing analysis report",

  // Suggestions
  suggestFollowUps: () => "Thinking of next steps...",
};

/**
 * Convert camelCase tool name to a title-cased, present-participle phrase.
 * e.g., "addCandidateSkill" → "Adding candidate skill"
 */
function camelToThinkingPhrase(toolName: string): string {
  // Split camelCase into words
  const words = toolName.replace(/([A-Z])/g, " $1").trim().toLowerCase().split(" ");
  if (words.length === 0) return "Working";

  // Convert first word to present participle (-ing form)
  const verb = words[0];
  const participle = toParticiple(verb);

  // Join remaining words
  const rest = words.slice(1).join(" ");
  return rest ? `${participle} ${rest}` : participle;
}

/**
 * Rough present-participle conversion for common verbs.
 */
function toParticiple(verb: string): string {
  if (verb.endsWith("e") && !verb.endsWith("ee")) {
    return verb.slice(0, -1) + "ing"; // create → creating, duplicate → duplicating
  }
  if (verb.endsWith("t") && verb.length <= 4) {
    return verb + "ting"; // get → getting, set → setting
  }
  return verb + "ing";
}

/**
 * Truncate a string with ellipsis.
 */
function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 1) + "…";
}

/**
 * Get a human-readable thinking summary for a tool invocation.
 *
 * @param toolName - The tool's programmatic name
 * @param input - The tool's input arguments (may be undefined during streaming)
 * @returns A concise, present-tense summary string
 */
export function getToolThinkingSummary(
  toolName: string,
  input: unknown
): string {
  const generator = customGenerators[toolName];
  if (generator) {
    return generator((input as ToolInput) ?? {});
  }
  // Fallback: camelCase → title phrase
  return camelToThinkingPhrase(toolName);
}
